import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Breadcrumb from '../components/common/Breadcrumb';
import ROUTES from '../constants/routes';
import { useToast } from '../components/contexts/ToastContext';
import apiService from '../services/apiService';
import { isOrdenTrabajoVencida, getDiasHastaOrdenTrabajo } from '../utils/dateHelpers';
import { prioridadBadge, prioridadBorder, formatEstado } from '../utils/otRenderHelpers';
import ConfirmDialog from '../components/common/ConfirmDialog';
import useConfirmAction from '../hooks/useConfirmAction';

/**
 * Vista de agenda del técnico — OTs agrupadas por urgencia (Atrasadas, Hoy, Próximas)
 *
 * Consume GET /api/ordenes-trabajo/mis-ordenes que filtra por token server-side.
 *
 * @returns {JSX.Element}
 */
function MisOrdenesTrabajo() {
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [ordenes, setOrdenes] = useState([]);
  const [loading, setLoading] = useState(true);
  const iniciarOT = useConfirmAction();

  const fetchOrdenes = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiService.getMisOrdenesTrabajo({
        limit: 1000,
        sortBy: 'ot.fecha_ejecucion',
        sortOrder: 'asc'
      });
      if (response.success) {
        setOrdenes(response.data);
      }
    } catch {
      showToast('Error al cargar órdenes de trabajo', 'error');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchOrdenes(); }, [fetchOrdenes]);

  // ── Agrupar OTs activas por urgencia ──
  const sections = { atrasadas: [], hoy: [], proximas: [] };
  ordenes.forEach(ot => {
    if (ot.estado !== 'programado' && ot.estado !== 'en_progreso') return;
    const dias = getDiasHastaOrdenTrabajo(ot.fecha_ejecucion);
    if (dias === null) return;
    if (dias < 0) sections.atrasadas.push(ot);
    else if (dias === 0) sections.hoy.push(ot);
    else sections.proximas.push(ot);
  });

  const hasAny = sections.atrasadas.length > 0 || sections.hoy.length > 0 || sections.proximas.length > 0;

  const handleSelectOT = (ot) => {
    if (ot.estado === 'programado') {
      iniciarOT.open(ot);
    } else {
      navigate(`/mis-ordenes-trabajo/${ot.id}`);
    }
  };

  const handleConfirmIniciar = async () => {
    const ot = iniciarOT.state.item;
    iniciarOT.setLoading(true);
    try {
      const response = await apiService.iniciarOrdenTrabajo(ot.id);
      if (response.success) {
        showToast('Orden de trabajo iniciada', 'success');
        iniciarOT.reset();
        navigate(`/mis-ordenes-trabajo/${ot.id}`);
      } else {
        showToast(response.message || 'Error al iniciar la orden', 'error');
        iniciarOT.setLoading(false);
      }
    } catch {
      showToast('Error al iniciar la orden de trabajo', 'error');
      iniciarOT.setLoading(false);
    }
  };

  return (
    <>
      <Navbar />
      <div className="main-content">
        <Breadcrumb items={[
          { label: 'Inicio', to: ROUTES.HOME },
          { label: 'Mis OTs' }
        ]} />

        {loading ? (
          <div className="ot-agenda-loading">
            <i className="fas fa-spinner fa-spin" /> Cargando...
          </div>
        ) : !hasAny ? (
          <div className="ot-agenda-empty">
            <i className="fas fa-clipboard-check" />
            <p>No tienes órdenes de trabajo pendientes</p>
          </div>
        ) : (
          <>
            <AgendaSection
              title="Atrasadas"
              icon="fa-triangle-exclamation"
              variant="danger"
              badgeClass="badge-danger"
              items={sections.atrasadas}
              onSelectOT={handleSelectOT}
            />
            <AgendaSection
              title="Hoy"
              icon="fa-sun"
              variant="warning"
              badgeClass="badge-warning"
              items={sections.hoy}
              onSelectOT={handleSelectOT}
            />
            <AgendaSection
              title="Próximas"
              icon="fa-calendar-days"
              variant="neutral"
              badgeClass="badge-secondary"
              items={sections.proximas}
              onSelectOT={handleSelectOT}
            />
          </>
        )}
      </div>

      <ConfirmDialog
        isOpen={iniciarOT.state.open}
        title="Iniciar Orden de Trabajo"
        message={
          iniciarOT.state.item
            ? `¿Deseas iniciar la orden ${iniciarOT.state.item.codigo_ot}? Se registrará la fecha y hora de inicio.`
            : ''
        }
        confirmText="Iniciar"
        onConfirm={handleConfirmIniciar}
        onCancel={iniciarOT.close}
        variant="info"
        loading={iniciarOT.state.loading}
      />
    </>
  );
}

// ── Sub-components ──────────────────────────────────────────────

/**
 * Sección de agenda (Atrasadas, Hoy, Próximas)
 *
 * @param {Object} props
 * @param {string} props.title - Título de la sección
 * @param {string} props.icon - Clase FontAwesome del ícono
 * @param {string} props.variant - Variante de color (danger, warning, neutral)
 * @param {string} props.badgeClass - Clase CSS del badge de conteo
 * @param {Array} props.items - OTs de esta sección
 * @param {Function} props.onSelectOT - Callback al seleccionar una OT
 * @returns {JSX.Element|null}
 */
function AgendaSection({ title, icon, variant, badgeClass, items, onSelectOT }) {
  if (items.length === 0) return null;

  return (
    <section className="ot-agenda-section">
      <div className={`ot-section-header ot-section-header--${variant}`}>
        <h2 className="ot-section-title">
          <i className={`fas ${icon}`} />
          {title}
        </h2>
        <span className={`badge ${badgeClass}`}>{items.length}</span>
      </div>
      <div className="ot-cards-grid">
        {items.map((ot, i) => (
          <OTCard key={ot.id} ot={ot} index={i} onSelect={onSelectOT} />
        ))}
        {/* Skeleton placeholders para rellenar el grid de 3 columnas */}
        {Array.from({ length: (3 - (items.length % 3)) % 3 }, (_, i) => (
          <div
            key={`skeleton-${i}`}
            className="ot-card-skeleton"
            aria-hidden="true"
            style={{ animationDelay: `${0.05 + (items.length + i) * 0.07}s` }}
          />
        ))}
      </div>
    </section>
  );
}

/**
 * Formatea fecha a formato corto español (ej: "5 mar 2026")
 *
 * @param {string|Date} fecha
 * @returns {string}
 */
function formatFechaCorta(fecha) {
  if (!fecha) return '-';
  return new Date(fecha).toLocaleDateString('es-CL', {
    day: 'numeric', month: 'short', year: 'numeric'
  });
}

/**
 * Tarjeta de orden de trabajo para la vista agenda
 *
 * @param {Object} props
 * @param {Object} props.ot - Datos de la orden de trabajo
 * @param {number} props.index - Índice para animation delay
 * @param {Function} props.onSelect - Callback al seleccionar
 * @returns {JSX.Element}
 */
function OTCard({ ot, index, onSelect }) {
  const dias = getDiasHastaOrdenTrabajo(ot.fecha_ejecucion);
  const isOverdue = isOrdenTrabajoVencida(ot.fecha_ejecucion, ot.estado);
  const borderClass = prioridadBorder[ot.prioridad] || '';

  const total = ot.actividades_total || 0;
  const completadas = ot.actividades_completadas || 0;
  const pct = total > 0 ? Math.round((completadas / total) * 100) : 0;

  let fillClass = '';
  if (pct === 100) fillClass = 'mant-progress-fill-complete';
  else if (pct > 0) fillClass = 'mant-progress-fill-partial';

  return (
    <article
      className={`mobile-card ot-card ${borderClass} ${isOverdue ? 'ot-card-overdue' : ''}`}
      tabIndex={0}
      onClick={() => onSelect(ot)}
      onKeyDown={(e) => { if (e.key === 'Enter') onSelect(ot); }}
      style={{ animationDelay: `${0.05 + index * 0.07}s` }}
    >
      <div className="mobile-card-header">
        <span className="mobile-card-title">{ot.codigo_ot}</span>
        <div className="ot-card-badges">
          {isOverdue && dias !== null && (
            <span className="badge-vencido badge-vencido--alert">
              <i className="fas fa-clock-rotate-left" /> {Math.abs(dias)} día{Math.abs(dias) !== 1 ? 's' : ''}
            </span>
          )}
          <span className={`badge ${prioridadBadge[ot.prioridad] || ''}`}>
            {ot.prioridad ? ot.prioridad.charAt(0).toUpperCase() + ot.prioridad.slice(1) : ''}
          </span>
        </div>
      </div>

      <div className="ot-card-equipo">
        <p className="ot-card-equipo-name">{ot.equipo?.marca} {ot.equipo?.modelo}</p>
        <span className="ot-card-equipo-code">{ot.equipo?.codigo}</span>
      </div>

      <div className="mobile-card-body">
        <div className="mobile-card-field">
          <span className="mobile-card-label"><i className="fas fa-clipboard-list" /> Plantilla</span>
          <span className="mobile-card-value">{ot.plantilla?.nombre || <span className="text-muted">-</span>}</span>
        </div>
        <div className="mobile-card-field">
          <span className="mobile-card-label"><i className="fas fa-wrench" /> Tipo</span>
          <span className="mobile-card-value">{ot.tipo_mantenimiento?.nombre}</span>
        </div>
        <div className="mobile-card-field">
          <span className="mobile-card-label"><i className="fas fa-circle-half-stroke" /> Estado</span>
          <span className="mobile-card-value ot-status-value">
            <span className={`status-dot ot-status-dot-${ot.estado}`} />
            {formatEstado(ot.estado)}
          </span>
        </div>
        <div className="mobile-card-field">
          <span className="mobile-card-label"><i className="far fa-calendar" /> Fecha</span>
          <span className="mobile-card-value">{formatFechaCorta(ot.fecha_ejecucion)}</span>
        </div>
      </div>

      <div className="ot-card-progress">
        <div className="mant-progress-header">
          <span className="mant-progress-text">
            <i className="fas fa-list-check ot-icon-gap" /> Actividades
          </span>
          <span className={`mant-progress-text ot-progress-count ${total === 0 ? 'ot-progress-count--zero' : ''}`}>
            {completadas} / {total}
          </span>
        </div>
        <div className="mant-progress-bar" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
          <div className={`mant-progress-fill ${fillClass}`} style={{ width: `${pct}%` }} />
        </div>
      </div>

      <div className="card-actions">
        <button
          className="btn btn-primary btn-sm"
          onClick={(e) => { e.stopPropagation(); onSelect(ot); }}
        >
          Ver OT <i className="fas fa-arrow-right" />
        </button>
      </div>
    </article>
  );
}

export default MisOrdenesTrabajo;
